'use client';

import { useTranslations } from 'next-intl';

import styles from './FullShareView.module.css';
import { FullShareViewFooter } from './FullShareViewFooter';
import { FullShareViewFootnote } from './FullShareViewFootnote';
import { FullShareViewHeader } from './FullShareViewHeader';
import { FullShareViewSiteDetails } from './FullShareViewSiteDetails';
import { NotificationBanner } from './NotificationBanner';
import { ShareLinkField } from './ShareLinkField';
import { useCopyShareLink } from './useCopyShareLink';

import type { ShareModalClientProps } from './ShareModalClient';

export function FullShareView({
  shareUrl,
  siteName,
  imageUrl,
  siteUrl,
  ownerUrl,
  onClose,
  showFootnote = true,
}: Omit<ShareModalClientProps, 'origin' | 'commentsCount' | 'unreadCount'>) {
  const t = useTranslations();
  const { copied, copyError, handleClose, handleCopyLink } = useCopyShareLink(
    shareUrl,
    onClose,
  );

  return (
    <div
      data-hook="share-lb"
      className="flex w-[600px] flex-col overflow-hidden rounded-xl bg-background"
      style={{
        maxHeight: 'min(627px, calc(100vh))',
        boxShadow: '0 20px 30px 0 rgba(19,23,32,.1)',
      }}
    >
      <FullShareViewHeader
        title={t('share-title')}
        subtitle={t('share-subtitle')}
        closeLabel={t('share-cancel')}
        onClose={handleClose}
      />

      <div
        className={`min-h-0 flex-1 overflow-y-auto px-6 py-6 flex flex-col gap-6 ${styles.scrollable}`}
      >
        <FullShareViewSiteDetails
          siteName={siteName}
          imageUrl={imageUrl}
          siteUrl={siteUrl}
        />

        {copyError ? (
          <NotificationBanner
            variant="destructive"
            dataHook="notifications-copy-error"
            text={t('share-sendError')}
          />
        ) : null}

        <ShareLinkField shareUrl={shareUrl} label={t('share-linkLabel')} />
      </div>

      <FullShareViewFooter
        cancelLabel={t('share-cancel')}
        copyLabel={copied ? t('share-linkCopied') : t('share-copy')}
        copied={copied}
        shareUrl={shareUrl}
        onClose={handleClose}
        onCopy={handleCopyLink}
      />

      {showFootnote ? (
        <FullShareViewFootnote
          text={t('share-footnoteText')}
          ctaLabel={t('share-viewComments')}
          ownerUrl={ownerUrl}
        />
      ) : null}
    </div>
  );
}
